// Incoming-call ringtone. Primary path is a real audio file played through an
// HTMLAudioElement (reliable on iOS once unlocked by a user gesture). A Web Audio
// oscillator is kept as a fallback for when the element can't play.
//
// Both paths are subject to the iOS hardware mute switch: if the phone's silent
// switch is ON, the browser plays NO sound. Nothing on the web can override that.

type WindowWithWebkitAudio = Window & { webkitAudioContext?: typeof AudioContext };

const RINGTONE_URL = `${import.meta.env.BASE_URL}sounds/ringtone.wav`;

// ---- HTMLAudioElement (primary) ----

let el: HTMLAudioElement | null = null;

function getEl(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  if (!el) {
    try {
      el = new Audio(RINGTONE_URL);
      el.loop = true;
      el.preload = 'auto';
    } catch {
      return null;
    }
  }
  return el;
}

// ---- Web Audio oscillator (fallback) ----

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

/**
 * Unlock playback. Call on user gestures and when the page returns to the
 * foreground. Plays the element muted-then-pauses (the iOS unlock trick) and
 * resumes the fallback AudioContext.
 */
export function primeRingtone(): void {
  const a = getEl();
  if (a) {
    const wasMuted = a.muted;
    a.muted = true;
    const p = a.play();
    if (p && typeof p.then === 'function') {
      void p
        .then(() => {
          a.pause();
          a.currentTime = 0;
          a.muted = wasMuted;
        })
        .catch(() => {
          a.muted = wasMuted; // not unlocked yet — will retry on next gesture
        });
    } else {
      a.pause();
      a.currentTime = 0;
      a.muted = wasMuted;
    }
  }
  const c = getCtx();
  if (c) void c.resume().catch(() => { /* still no gesture */ });
}

// Intended ring state. Guards the ASYNC start paths (blocked-element fallback,
// AudioContext.resume) so a start that resolves AFTER stopRingtone() was called
// does not begin ringing with nothing left to stop it (→ rings forever).
let wantRinging = false;

let oscActive: { stop: () => void } | null = null;

function startOscillator(): void {
  stopOscillator();
  const c = getCtx();
  if (!c) return;
  const build = () => {
    // Same orphan guard as callerTone.ts: two builds parked on resume() must
    // not leave the first oscillator running with no handle to stop it.
    stopOscillator();
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain);
    gain.connect(c.destination);
    gain.gain.value = 0.12;
    osc.frequency.value = 440;

    let playing = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const loop = () => {
      if (!playing) return;
      osc.frequency.value = 440;
      timers.push(setTimeout(() => {
        if (!playing) return;
        osc.frequency.value = 550;
        timers.push(setTimeout(() => { if (playing) loop(); }, 400));
      }, 400));
    };
    try { osc.start(); } catch { /* already started */ }
    loop();

    oscActive = {
      stop: () => {
        playing = false;
        timers.forEach(clearTimeout);
        try { osc.stop(); } catch { /* already stopped */ }
        try { osc.disconnect(); gain.disconnect(); } catch { /* noop */ }
      },
    };
  };
  if (c.state === 'suspended') {
    void c.resume().then(() => { if (wantRinging) build(); }).catch(() => {});
  } else {
    build();
  }
}

function stopOscillator(): void {
  oscActive?.stop();
  oscActive = null;
}

/** Start the looping ringtone. Idempotent — replaces any current ring. */
export function startRingtone(): void {
  stopRingtone();
  wantRinging = true;
  const a = getEl();
  if (!a) {
    startOscillator();
    return;
  }
  a.currentTime = 0;
  a.muted = false;
  const p = a.play();
  if (p && typeof p.then === 'function') {
    void p
      .then(() => {
        // A stopRingtone() that raced ahead of this resolving play must win.
        if (!wantRinging) { try { a.pause(); a.currentTime = 0; } catch { /* noop */ } }
      })
      .catch(() => {
        // Element playback blocked — fall back to the oscillator, but ONLY if a
        // stop hasn't been requested meanwhile (else it would ring forever).
        if (wantRinging) startOscillator();
      });
  }
}

/** Stop the current ring (safe to call when none is playing). */
export function stopRingtone(): void {
  wantRinging = false;
  const a = el;
  if (a) {
    try { a.pause(); a.currentTime = 0; } catch { /* noop */ }
  }
  stopOscillator();
}

/**
 * Short two-tone alert (~0.4s), one-shot. Used for the promo-ending warning.
 * Reuses the fallback AudioContext; no-op if unavailable. Subject to the iOS
 * hardware mute switch, like the ringtone.
 */
export function playPromoAlert(): void {
  const c = getCtx();
  if (!c) return;
  const beep = (freq: number, startOffset: number, dur: number): void => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain);
    gain.connect(c.destination);
    gain.gain.value = 0.12;
    osc.frequency.value = freq;
    try {
      osc.start(c.currentTime + startOffset);
      osc.stop(c.currentTime + startOffset + dur);
    } catch {
      /* already started/stopped */
    }
  };
  const run = (): void => {
    beep(660, 0, 0.18);
    beep(880, 0.22, 0.18);
  };
  if (c.state === 'suspended') {
    void c.resume().then(run).catch(() => {});
  } else {
    run();
  }
}
